import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Link,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import { Phone, Lock, Verified } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { postRequest } from '../../services/api';

const steps = ['Enter Mobile', 'Verify OTP', 'Reset Password'];

const ForgotPassword = () => {
  const navigate = useNavigate();

  const [activeStep, setActiveStep] = useState(0);
  const [mobileNumber, setMobileNumber] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);

  // OTP countdown timer
  useEffect(() => {
    let interval;
    if (otpTimer > 0) {
      interval = setInterval(() => {
        setOtpTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [otpTimer]);

  // Step 1: Send OTP
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!/^[0-9]{10}$/.test(mobileNumber)) {
      setError('Please enter a valid 10-digit mobile number');
      return;
    }

    setLoading(true);

    try {
      await postRequest('users/forgot-password', {
        mobileNumber,
      });
      setSuccess('OTP sent successfully to your mobile number!');
      setOtpTimer(120);
      setTimeout(() => {
        setActiveStep(1);
        setSuccess('');
      }, 1000);
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(
        error.response?.data?.message ||
          error.response?.data?.error?.message ||
          'Failed to send OTP. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const otpCode = otp.join('');
    if (otpCode.length !== 4) {
      setError('Please enter the complete 4-digit OTP');
      return;
    }

    setLoading(true);

    try {
      // Just verify by attempting to check OTP through the reset endpoint directly
      // The actual verification happens in step 3 combined with reset
      await postRequest('users/verify-forgot-otp', {
        mobileNumber,
        otp: parseInt(otpCode),
      });
      setSuccess('OTP verified!');
      setActiveStep(2);
    } catch (error) {
      console.error('OTP verification error:', error);
      setError('Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      await postRequest('users/reset-password', {
        mobileNumber,
        otp: parseInt(otp.join('')),
        newPassword,
      });

      setSuccess('Password reset successful! Redirecting to login...');
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (error) {
      console.error('Reset password error:', error);
      setError(
        error.response?.data?.message ||
          error.response?.data?.error?.message ||
          'Failed to reset password. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setError('');
    setLoading(true);

    try {
      await postRequest('users/forgot-password', { mobileNumber });
      setSuccess('OTP resent successfully!');
      setOtpTimer(120);
      setOtp(['', '', '', '']);
    } catch (error) {
      console.error('Resend OTP error:', error);
      setError('Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    if (value && !/^\d+$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1);
    setOtp(newOtp);
    if (value && index < 3) {
      const nextInput = document.getElementById(`fp-otp-input-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`fp-otp-input-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #006994 0%, #00acc1 100%)',
        py: 4,
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={24}
          sx={{
            p: 4,
            borderRadius: 2,
          }}
        >
          <Box sx={{ mb: 3, textAlign: 'center' }}>
            <Lock sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
            <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
              Forgot Password
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Reset your password in 3 easy steps
            </Typography>
          </Box>

          <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Step 1: Enter Mobile Number */}
          {activeStep === 0 && (
            <form onSubmit={handleSendOtp}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                margin="normal"
                placeholder="Enter 10-digit mobile number"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <Phone color="action" />
                    </Box>
                  ),
                }}
                disabled={loading}
                inputProps={{ maxLength: 10 }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Send OTP'}
              </Button>

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Remember your password?{' '}
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => navigate('/login')}
                    sx={{ cursor: 'pointer' }}
                  >
                    Sign in
                  </Link>
                </Typography>
              </Box>
            </form>
          )}

          {/* Step 2: Verify OTP */}
          {activeStep === 1 && (
            <form onSubmit={handleVerifyOtp}>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                Enter the 4-digit OTP sent to <strong>{mobileNumber}</strong>
              </Typography>

              <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', mb: 3 }}>
                {otp.map((digit, index) => (
                  <TextField
                    key={index}
                    id={`fp-otp-input-${index}`}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleOtpKeyDown(index, e)}
                    inputProps={{
                      maxLength: 1,
                      style: {
                        textAlign: 'center',
                        fontSize: '1.5rem',
                        width: '3rem',
                        height: '3rem',
                        padding: '0.5rem',
                      },
                    }}
                    variant="outlined"
                    disabled={loading}
                    autoFocus={index === 0}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                ))}
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 1, mb: 2, py: 1.5 }}
                disabled={loading || otp.join('').length !== 4}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify OTP'}
              </Button>

              <Box sx={{ textAlign: 'center', mt: 2 }}>
                {otpTimer > 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Resend OTP in <strong>{otpTimer}</strong> seconds
                  </Typography>
                ) : (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={handleResendOtp}
                    sx={{ cursor: 'pointer' }}
                    disabled={loading}
                  >
                    Resend OTP
                  </Link>
                )}
              </Box>
            </form>
          )}

          {/* Step 3: Reset Password */}
          {activeStep === 2 && (
            <form onSubmit={handleResetPassword}>
              <TextField
                fullWidth
                label="New Password"
                type={showPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="normal"
                placeholder="Enter new password"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <Lock color="action" />
                    </Box>
                  ),
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        size="small"
                        onClick={() => setShowPassword(!showPassword)}
                        sx={{ minWidth: 'auto', textTransform: 'none' }}
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </Button>
                    </Box>
                  ),
                }}
                disabled={loading}
                helperText="Minimum 6 characters"
              />

              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="normal"
                placeholder="Re-enter new password"
                InputProps={{
                  startAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      <Verified color="action" />
                    </Box>
                  ),
                  endAdornment: (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Button
                        size="small"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        sx={{ minWidth: 'auto', textTransform: 'none' }}
                      >
                        {showConfirmPassword ? 'Hide' : 'Show'}
                      </Button>
                    </Box>
                  ),
                }}
                disabled={loading}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading || !newPassword || !confirmPassword}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}
        </Paper>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="body2" color="white">
            © 2026 Friends Services. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default ForgotPassword;